import { useQuery } from "@apollo/client";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { GridPaginationModel, GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
// Deep import from `src/utils/...` bypasses the package's top-level
// barrel, which transitively pulls in `encamp-prisma/generated/client`
// (server-only). The Vite `prisma-client-guard` plugin would otherwise
// fail the browser bundle. Other oak callers use this same pattern —
// see `Omnisearch.tsx` / `FilterPopover.tsx`.
import { extractKeyedSearchTerms } from "encamp-omnisearch/src/utils/keyedSearchTerms";
import { FeatureFlagKey, isFeatureFlagKey } from "encamp-shared/src/featureFlags";
import { DataGridToolbar, useGridPersistence, useOmnisearchState } from "hooks/omnisearch-v2";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useState } from "react";

import { gql } from "../../../generated-graphql";
import { StaffFlagsListQuery } from "../../../generated-graphql/graphql";
import { stripModuleSuffix } from "./flagAdoption";
import { FlagDetailDrawer } from "./FlagDetailDrawer";
import { buildFlagColumns, TYPE_LABEL, TYPE_FILTER_VALUES } from "./flagsGridColumns";
import { useCustomerTenantCount } from "./useCustomerTenantCount";

type GridSortItem = GridSortModel[number];

type FeatureFlagFragment = StaffFlagsListQuery["featureFlags"][number];

/**
 * Pulls the full set of feature flags in one shot. There are fewer than
 * ~50 flags total, so we don't paginate, filter, or sort on the server —
 * everything happens client-side via `applyFlagFilters` and
 * `sortFlagRows`. Intentionally takes no variables so the GraphQL
 * `NoUnusedVariablesRule` is satisfied (we deliberately avoid using
 * `OmnisearchDataDisplay`, which always injects `$search/$page/...`).
 */
const STAFF_FLAGS_LIST_QUERY = gql(`
  query StaffFlagsList {
    featureFlags {
      key
      label
      description
      type
      parentKey
      defaultValue
      staffOverride
      inDevelopment
      requires
      notes
      tags
      annotationUpdatedAt
      annotationUpdatedBy
      linearProjectUrl
      anticipatedReleaseDate
      annotationCreatedAt
      annotationCreatedBy
      tenantOnCount
      tenantOffCount
      subscriptionLabel
      subscriptionDiscrepancyCount
      subscriptionDiscrepancyBreakdown {
        flagOnSubOffCount
        flagOffSubOnCount
      }
    }
  }
`);

/**
 * The on-screen flag row. Mirrors the Apollo result type but with a
 * narrowed `key` (the key column is the row id, and we want callers like
 * `FlagDetailDrawer` to be able to assume it's a real `FeatureFlagKey`).
 */
export type FlagRow = {
  key: FeatureFlagKey;
  label: string;
  description: string;
  type: "MODULE" | "ADD_ON" | "OTHER";
  parentKey: string | null;
  parentLabel: string | null;
  defaultValue: boolean;
  staffOverride: boolean;
  inDevelopment: boolean;
  requires: string[];
  notes: string | null;
  tags: string[];
  annotationUpdatedAt: string | null;
  annotationUpdatedBy: string | null;
  /**
   * URL of the Linear project delivering this flag's work, staff-pasted
   * and validated server-side. Null when no project is linked. This grid
   * row never triggers a Linear API call — that's the drawer-only
   * `linearProject` lookup — so we only carry the raw URL here.
   */
  linearProjectUrl: string | null;
  /**
   * Target GA date for this flag. Defaulted in the drawer UI from the
   * linked Linear project's target date but manually overridable.
   */
  anticipatedReleaseDate: string | null;
  annotationCreatedAt: string | null;
  annotationCreatedBy: string | null;
  tenantOnCount: number;
  tenantOffCount: number;
  /**
   * Server-supplied label for the subscription module backing this
   * flag (e.g. `"RCRA / Waste"`). `null` when the flag has no
   * subscription mapping — the frontend uses null/non-null as the
   * single "is this flag subscription-aware?" signal so no flag-key
   * → column mapping needs to live on the client.
   */
  subscriptionLabel: string | null;
  /**
   * Count of active customer tenants whose flag value disagrees with
   * their Salesforce-synced subscription status for the matching
   * module. `null` when the flag has no subscription mapping (e.g.
   * Platform, Scout, most non-module flags) so the grid cell can
   * render blank. `0` means a mapping exists and everything is
   * aligned — distinct UX from "blank".
   */
  subscriptionDiscrepancyCount: number | null;
  subscriptionDiscrepancyBreakdown: {
    flagOnSubOffCount: number;
    flagOffSubOnCount: number;
  } | null;
  hasAnnotation: boolean;
};

/**
 * Filter chips wired into the omnisearch popover. All are evaluated
 * client-side by `applyFlagFilters` below; nothing is forwarded to the
 * server because the server-side resolver doesn't accept these args.
 */
const FLAG_FILTER_COLUMNS: FilterKey[] = [
  {
    key: "type",
    header: "Type",
    filterKeyType: "enumAutocomplete",
    enumValues: TYPE_FILTER_VALUES,
    enumPresentationFunction: (v) => TYPE_LABEL[v as FlagRow["type"]] ?? v,
  },
  { key: "staffOnly", header: "Staff-visible", filterKeyType: "boolean" },
  { key: "inDevelopment", header: "In development", filterKeyType: "boolean" },
  { key: "defaultOn", header: "Default on", filterKeyType: "boolean" },
  { key: "hasAnnotation", header: "Has annotation", filterKeyType: "boolean" },
];

function matchesBoolean(filterValue: string | undefined, rowValue: boolean): boolean {
  if (filterValue === undefined) return true;
  return filterValue === "true" ? rowValue : !rowValue;
}

/**
 * Apply omnisearch chips + free-text search to the in-memory flag set.
 * Filtering happens entirely client-side — there are fewer than ~50
 * flags so even a naive sweep is cheap, and the GraphQL resolver doesn't
 * accept these args anyway.
 *
 * Parses the raw omnisearch string here (rather than receiving an
 * already-parsed result) because we also want the free-text portion —
 * `extractKeyedSearchTerms` returns `[keyValuePairs, freeText]` in one
 * pass, and re-using it keeps the chip semantics aligned with the rest
 * of the app's omnisearch behavior.
 */
export function applyFlagFilters(flags: FlagRow[], omnisearch: string): FlagRow[] {
  const [keyValuePairs, freeTextRaw] = extractKeyedSearchTerms(omnisearch);
  // Group keyed values by key; multi-value chips (e.g. type:MODULE|ADD_ON)
  // become arrays here.
  const byKey = new Map<string, string[]>();
  for (const { key, value } of keyValuePairs) {
    const existing = byKey.get(key) ?? [];
    byKey.set(key, [...existing, ...value]);
  }
  const types = byKey.get("type") ?? [];
  const staffOnly = byKey.get("staffOnly")?.[0];
  const inDev = byKey.get("inDevelopment")?.[0];
  const defaultOn = byKey.get("defaultOn")?.[0];
  const hasAnnotation = byKey.get("hasAnnotation")?.[0];
  const tagFilter = byKey.get("tag") ?? [];
  const freeText = freeTextRaw.trim().toLowerCase();

  return flags.filter((flag) => {
    if (types.length > 0 && !types.includes(flag.type)) return false;
    if (!matchesBoolean(staffOnly, flag.staffOverride)) return false;
    if (!matchesBoolean(inDev, flag.inDevelopment)) return false;
    if (!matchesBoolean(defaultOn, flag.defaultValue)) return false;
    if (!matchesBoolean(hasAnnotation, flag.hasAnnotation)) return false;
    if (tagFilter.length > 0 && !tagFilter.some((t) => flag.tags.includes(t))) return false;
    if (freeText.length > 0) {
      const haystack = `${flag.label} ${flag.key} ${flag.description}`.toLowerCase();
      if (!haystack.includes(freeText)) return false;
    }
    return true;
  });
}

/**
 * Sort the in-memory rows. We deliberately avoid the DataGrid's
 * built-in client-side sort to keep the row order stable across
 * filter / pagination changes (and so the tests can lock the order).
 */
export function sortFlagRows(rows: FlagRow[], sortItem: GridSortItem | undefined): FlagRow[] {
  if (!sortItem) return rows;
  const { field, sort } = sortItem;
  if (!sort) return rows;
  const dir = sort === "asc" ? 1 : -1;
  const cmpString = (a: string | null | undefined, b: string | null | undefined): number => {
    const av = (a ?? "").toLowerCase();
    const bv = (b ?? "").toLowerCase();
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  };
  const cmpNumber = (a: number, b: number): number => (a - b) * dir;
  const cmpBool = (a: boolean, b: boolean): number => cmpNumber(a ? 1 : 0, b ? 1 : 0);
  const sorted = [...rows];
  sorted.sort((a, b) => {
    switch (field) {
      case "label":
        return cmpString(a.label, b.label);
      case "type":
        return cmpString(a.type, b.type);
      case "parentLabel":
        return cmpString(a.parentLabel, b.parentLabel);
      case "tenantOnCount":
        return cmpNumber(a.tenantOnCount, b.tenantOnCount);
      case "subscriptionDiscrepancyCount": {
        // Null-last regardless of sort direction: rows without a
        // subscription mapping (the cell renders blank) should never
        // sort interleaved with mappable rows.
        const aNull = a.subscriptionDiscrepancyCount === null;
        const bNull = b.subscriptionDiscrepancyCount === null;
        if (aNull && bNull) return 0;
        if (aNull) return 1;
        if (bNull) return -1;
        return cmpNumber(a.subscriptionDiscrepancyCount ?? 0, b.subscriptionDiscrepancyCount ?? 0);
      }
      case "annotationUpdatedAt":
        return cmpString(a.annotationUpdatedAt, b.annotationUpdatedAt);
      case "annotationCreatedAt":
        // ISO timestamps sort correctly as strings.
        return cmpString(a.annotationCreatedAt, b.annotationCreatedAt);
      case "staffOverride":
        return cmpBool(a.staffOverride, b.staffOverride);
      case "inDevelopment":
        return cmpBool(a.inDevelopment, b.inDevelopment);
      case "defaultValue":
        return cmpBool(a.defaultValue, b.defaultValue);
      default:
        return 0;
    }
  });
  return sorted;
}

/**
 * Convert the GraphQL response into the on-screen `FlagRow` shape used
 * by the grid + drawer. We resolve `parentLabel` here so the parent-
 * module column can render and sort without a second lookup per row.
 *
 * For grouping purposes a module is its own "parent" — every flag that
 * belongs to a module (including the module itself) shares the same
 * value in the Parent module column. We also strip the trailing
 * " module" word from the displayed label since the column header
 * already says "Parent module" (e.g. "Waste module" → "Waste").
 */
export function toFlagRows(apolloFlags: ReadonlyArray<FeatureFlagFragment>): FlagRow[] {
  // The parent's label comes from the same query result — no metadata lookup.
  const labelByKey = new Map(apolloFlags.map((f) => [f.key, f.label]));
  return apolloFlags
    .filter((f) => isFeatureFlagKey(f.key))
    .map((f) => {
      const parentLabelRaw =
        f.parentKey && isFeatureFlagKey(f.parentKey)
          ? (labelByKey.get(f.parentKey) ?? f.parentKey)
          : f.type === "MODULE"
            ? f.label
            : null;
      const parentLabel = parentLabelRaw ? stripModuleSuffix(parentLabelRaw) : null;
      const hasAnnotation =
        (f.notes !== null && f.notes !== undefined && f.notes.trim().length > 0) ||
        f.tags.length > 0;
      return {
        // Narrow `key` from the GraphQL `String!` to the typed enum so
        // downstream consumers (drawer, dependency lookups) don't need
        // to re-validate.
        key: f.key as FeatureFlagKey,
        label: f.label,
        description: f.description,
        type: f.type as FlagRow["type"],
        parentKey: f.parentKey ?? null,
        parentLabel,
        defaultValue: f.defaultValue,
        staffOverride: f.staffOverride,
        inDevelopment: f.inDevelopment,
        requires: [...f.requires],
        notes: f.notes ?? null,
        tags: [...f.tags],
        annotationUpdatedAt: f.annotationUpdatedAt ?? null,
        annotationUpdatedBy: f.annotationUpdatedBy ?? null,
        linearProjectUrl: f.linearProjectUrl ?? null,
        anticipatedReleaseDate: f.anticipatedReleaseDate ?? null,
        annotationCreatedAt: f.annotationCreatedAt ?? null,
        annotationCreatedBy: f.annotationCreatedBy ?? null,
        tenantOnCount: f.tenantOnCount,
        tenantOffCount: f.tenantOffCount,
        subscriptionLabel: f.subscriptionLabel ?? null,
        subscriptionDiscrepancyCount: f.subscriptionDiscrepancyCount ?? null,
        subscriptionDiscrepancyBreakdown: f.subscriptionDiscrepancyBreakdown
          ? {
              flagOnSubOffCount: f.subscriptionDiscrepancyBreakdown.flagOnSubOffCount,
              flagOffSubOnCount: f.subscriptionDiscrepancyBreakdown.flagOffSubOnCount,
            }
          : null,
        hasAnnotation,
      };
    });
}

const PAGE_SIZE = 50; // every flag fits on one page; pagination is a no-op
const PAGE_SIZE_OPTIONS = [PAGE_SIZE];
const PERSISTENCE_KEY = "staff-feature-flags-by-flag";
const INITIAL_SORT_MODEL: GridSortModel = [{ field: "label", sort: "asc" }];

/**
 * Columns that must not contribute an auto-derived free-text filter key.
 * Either their semantic equivalent already exists as a structured chip in
 * `additionalFilterColumns` (avoiding "Type / Type" duplicates in the
 * popover) or they don't make sense as a discrete filter at all —
 * free-text label, numeric counts, timestamps.
 */
const EXCLUDED_FILTER_FIELDS = [
  "label",
  "type",
  "staffOverride",
  "inDevelopment",
  "defaultValue",
  "tags",
  "tenantOnCount",
  "annotationCreatedAt",
  "annotationUpdatedAt",
];

/**
 * Derive the omnisearch popover's filter keys from the grid columns,
 * then append the page's hand-written chips.
 *
 * `DataGridToolbar` takes `columnFilterKeys` as a prop rather than
 * deriving them from `columns` — only the all-in-one
 * `OmnisearchDataGridV2` does that, and this page can't use it (see the
 * `FlagsTab` note). So the derivation that `useOmnisearchDatagrid` used
 * to perform internally lives here instead, matching its behavior:
 * skip excluded / unfilterable / action columns, then key off the field
 * and label off the header.
 */
export function deriveColumnFilterKeys(
  columns: OmnisearchGridColDef<FlagRow>[],
  additionalFilters: FilterKey[]
): FilterKey[] {
  const fromColumns = columns
    .filter(
      (col) =>
        !EXCLUDED_FILTER_FIELDS.includes(col.field) &&
        col.filterable !== false &&
        col.type !== "actions"
    )
    .map<FilterKey>((col) => ({
      key: col.field,
      header: col.headerName ?? col.field,
      filterKeyType: col.filterKeyType,
      enumValues: col.enumValues,
      enumPresentationFunction: col.enumPresentationFunction,
    }));
  return [...fromColumns, ...additionalFilters];
}

/**
 * Assembles the three pieces of grid state this page needs — URL-driven
 * omnisearch, localStorage-backed sort + column visibility, and
 * pagination — into the single call site `useOmnisearchDatagrid` used to
 * provide. `OmnisearchDataGridV2` owns all three internally, so a custom
 * layout has to wire them up itself.
 */
function useFlagsGridState(isMobile: boolean) {
  // The V2 store treats the `omnisearch` URL param as the single source of
  // truth and debounces its own writes, so this value is the equivalent of
  // V1's `debouncedOmnisearch` and is safe to filter on directly.
  // `DataGridToolbar` subscribes to the same store internally, so the
  // search input needs no wiring from here.
  const { omnisearch } = useOmnisearchState();

  const {
    pageSize,
    sortModel,
    columnVisibilityModel,
    setPageSize,
    setSortModel,
    setColumnVisibilityModel,
  } = useGridPersistence({
    persistenceKey: PERSISTENCE_KEY,
    initialSortModel: INITIAL_SORT_MODEL,
    initialPageSize: PAGE_SIZE,
    isMobile,
  });

  // Page is component-local rather than URL- or localStorage-backed, which
  // is what `OmnisearchDataGridV2` does internally too. Nothing is lost:
  // `PAGE_SIZE` exceeds the total flag count, so the footer stays hidden
  // and there's no control to move off page 0.
  const [page, setPage] = useState(0);
  const paginationModel = useMemo(() => ({ page, pageSize }), [page, pageSize]);
  const setPaginationModel = useCallback(
    (model: GridPaginationModel) => {
      setPage(model.page);
      setPageSize(model.pageSize);
    },
    [setPageSize]
  );

  return {
    omnisearch,
    sortModel,
    setSortModel,
    columnVisibilityModel,
    setColumnVisibilityModel,
    paginationModel,
    setPaginationModel,
  };
}

/**
 * Flag-centric (one row per flag) view of feature flags. Pairs with
 * `OrganizationsTab` under `?tab=flags` (the default).
 *
 * Built on the Omnisearch **V2** primitives for a custom layout —
 * `DataGridToolbar` + `useOmnisearchState` + `useGridPersistence` — which
 * is the path `hooks/omnisearch-v2/MIGRATION.md` prescribes when the
 * all-in-one `OmnisearchDataGridV2` doesn't fit.
 *
 * It doesn't fit here because V2's grid body is server-driven by
 * construction: `SuspenseDataGrid` pins `paginationMode="server"` and
 * `sortingMode="server"`, and takes its rows and total from
 * `getItems(data)` / `getCount(data)` — neither of which is handed the
 * omnisearch string, sort model, or page. This page has to filter, sort,
 * and page in memory: the `featureFlags` resolver accepts no arguments
 * (flags are code-defined constants joined to their DB annotations, not a
 * queryable table), and declared-but-unused GraphQL variables fail
 * server-side validation. Client-side is fine regardless — there are <50
 * flags total. Using the V2 toolbar still gets us V2's URL-driven search
 * store, localStorage grid persistence, and the built-in Manage Columns
 * popover.
 */
export function FlagsTab() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Track only the selected flag's key — never the row object — so that
  // when `StaffFlagsList` refetches (e.g. after saving an annotation)
  // the drawer reads the fresh row out of `flagsByKey` instead of the
  // stale snapshot captured at click time.
  const [selectedKey, setSelectedKey] = useState<FeatureFlagKey | null>(null);
  const { count: customerTenantCount } = useCustomerTenantCount();
  const { data, loading } = useQuery(STAFF_FLAGS_LIST_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const allRows = useMemo<FlagRow[]>(
    () => (data?.featureFlags ? toFlagRows(data.featureFlags) : []),
    [data]
  );

  const flagsByKey = useMemo(() => {
    const map = new Map<string, FlagRow>();
    for (const row of allRows) map.set(row.key, row);
    return map;
  }, [allRows]);

  const selectedFlag = selectedKey ? (flagsByKey.get(selectedKey) ?? null) : null;

  const columns = useMemo(() => buildFlagColumns(customerTenantCount), [customerTenantCount]);

  // Build the dynamic Tag chip lazily off the rows we have on hand. Avoids
  // a second round-trip to `getAllFeatureFlagTags` just for chip values —
  // any tag the user could pick from autocomplete is already attached to
  // some flag in the result set.
  const additionalFilterColumns = useMemo<FilterKey[]>(() => {
    const tagValues = Array.from(new Set(allRows.flatMap((r) => r.tags))).sort();
    return [
      ...FLAG_FILTER_COLUMNS,
      {
        key: "tag",
        header: "Tag",
        filterKeyType: "enumAutocomplete",
        enumValues: tagValues,
      },
    ];
  }, [allRows]);

  const columnFilterKeys = useMemo(
    () => deriveColumnFilterKeys(columns, additionalFilterColumns),
    [columns, additionalFilterColumns]
  );

  const {
    omnisearch,
    sortModel,
    setSortModel,
    columnVisibilityModel,
    setColumnVisibilityModel,
    paginationModel,
    setPaginationModel,
  } = useFlagsGridState(isMobile);

  const filtered = useMemo(() => applyFlagFilters(allRows, omnisearch), [allRows, omnisearch]);
  const sorted = useMemo(() => sortFlagRows(filtered, sortModel[0]), [filtered, sortModel]);
  // Even though pageSize ≥ flag count today, honoring the pagination
  // model keeps the grid behavior consistent with the rest of the app
  // and gives us headroom if we ever pass 50 flags.
  const start = paginationModel.page * paginationModel.pageSize;
  const paged = sorted.slice(start, start + paginationModel.pageSize);

  const handleRowClick = useCallback((params: GridRowParams<FlagRow>) => {
    setSelectedKey(params.row.key);
  }, []);

  return (
    // `px: 3` matches the horizontal padding the omnisearch grid wrappers
    // apply — keeps the grid flush with the rest of the staff datagrid
    // pages.
    <Box sx={{ px: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Shows all feature flags in the system and their adoption rates.
      </Typography>
      {/* Search input + Filters + Manage Columns. `shouldShowManageColumns`
          is left to the toolbar's own heuristic, which turns the button on
          for any grid with 5+ non-action columns — this one has 11. */}
      <DataGridToolbar
        columnFilterKeys={columnFilterKeys}
        persistenceKey={PERSISTENCE_KEY}
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
      />
      <DataGrid<FlagRow>
        rows={paged}
        columns={columns}
        getRowId={(row) => row.key}
        autoHeight
        getRowHeight={() => "auto"}
        loading={loading}
        pagination
        paginationMode="client"
        sortingMode="client"
        filterMode="client"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onRowClick={handleRowClick}
        sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
        hideFooterPagination={sorted.length <= PAGE_SIZE}
        rowCount={sorted.length}
      />

      <FlagDetailDrawer
        flag={selectedFlag}
        flagsByKey={flagsByKey}
        customerTenantCount={customerTenantCount}
        onSelectFlag={(f) => setSelectedKey(f.key)}
        onClose={() => setSelectedKey(null)}
      />
    </Box>
  );
}
